import { api } from "./api";
import {
  ProductPrice,
  Product,
  ProductVariant,
  CreateProduct,
  ProductPriceParams,
  ProductStatus,
  UpdateProduct,
} from "../types";
import { TAGS } from "../constants";

type ProductFilter =
  | {
      category?: string;
      to: number;
      from: number;
      status?: ProductStatus;
      gender?: string;
    }
  | { slug: string }
  | undefined;

export const productsApi = api.injectEndpoints({
  endpoints: (builder) => ({
    //products
    getProducts: builder.query<
      { data: Product[]; count: number },
      ProductFilter
    >({
      query: (params) => ({
        method: "GET",
        url: "/api/products",
        params,
      }),
      providesTags: [TAGS.PRODUCTS],
    }),

    getProduct: builder.query<Product, string>({
      query: (id) => ({
        method: "GET",
        url: `/api/products/${id}`,
      }),
      providesTags(_result, _error, arg) {
        return [{ type: TAGS.PRODUCT, id: arg }];
      },
    }),

    getPricesByProduct: builder.query<ProductPrice[], ProductPriceParams>({
      query: ({ slug, ...params }) => ({
        url: `/api/products/${slug}/prices`,
        method: "GET",
        params,
      }),
      providesTags: (_returns, _error, args) => [
        { type: TAGS.PRODUCT_PRICES, id: args.slug },
      ],
    }),
    getOneTimeProducts: builder.query<Product[], ProductFilter>({
      query: (params) => ({
        method: "GET",
        url: `/api/products/one-time`,
        params,
      }),
      providesTags: [TAGS.ONE_TIME_PRODUCTS],
    }),

    getProductVariants: builder.query<ProductVariant[], number>({
      query: (productId) => ({
        url: `/api/products/${productId}/variants`,
        method: "GET",
      }),
      providesTags: (_returns, _error, args) => [
        { type: TAGS.PRODUCT_VARIANTS, id: args },
      ],
    }),
    createProduct: builder.mutation<Product, CreateProduct>({
      query: (body) => ({ method: "POST", url: `/api/products`, body }),
      invalidatesTags: [TAGS.PRODUCTS],
    }),
    updateProduct: builder.mutation<Product, UpdateProduct>({
      query: ({ id, ...rest }) => ({
        method: "PUT",
        url: `/api/products/${id}`,
        body: rest,
      }),
      invalidatesTags: [TAGS.PRODUCTS],
    }),
    transferProducts: builder.mutation<{ message: string }, number[]>({
      query: (body) => ({
        method: "POST",
        url: `/api/products/transfer`,
        body,
      }),
    }),
  }),
});

export const {
  useGetProductsQuery,
  useGetPricesByProductQuery,
  useGetOneTimeProductsQuery,
  useGetProductVariantsQuery,
  useGetProductQuery,
  useCreateProductMutation,
  useUpdateProductMutation,
  useTransferProductsMutation,
} = productsApi;
